import { Button, Dropdown, MenuProps, Row, Tooltip } from "antd";
import { usePermissions } from "../../../lib/hooks/usePermissions";
import MAILING_PAGES from "../../../types/enums/MailingPages";
import { SendOutlined, EllipsisOutlined } from "@ant-design/icons";
import { useCallback, useMemo, useState, MouseEvent, ReactNode } from "react";
import { useResend } from "../../../components/EmailModal/hooks/useResend";
import { useDelete } from "../hooks/useDelete";
import { useSendOne } from "../hooks/useSendOne";
import { EMAIL_RULE } from "../../../lib/validationRules";

type TableActionsProps = {
  page: MAILING_PAGES;
  record: any;
  setId: React.Dispatch<React.SetStateAction<string | null>>;
  reloadData: () => void;
};

export const TableActions = ({ page, record, reloadData, setId }: TableActionsProps) => {
  const { hasAccess } = usePermissions();

  const [isSending, setIsSending] = useState<boolean>(false);

  const { send } = useSendOne(reloadData, setIsSending);

  const { resend } = useResend(reloadData, setIsSending);

  const { remove } = useDelete(page, reloadData);

  const onDelete = useCallback(
    (e: MouseEvent<HTMLInputElement>): void => {
      e.stopPropagation();
      remove(e.currentTarget.id);
    },
    [remove]
  );

  const onView = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      setId(e.currentTarget.id);
    },
    [setId]
  );

  const onResend = useCallback(
    (e: MouseEvent<HTMLInputElement>): void => {
      e.stopPropagation();
      !isSending && resend(e.currentTarget.id);
    },
    [isSending, resend]
  );

  const onSend = useCallback(
    (e: MouseEvent<any>) => {
      e.stopPropagation();
      send(e.currentTarget.id);
    },
    [send]
  );

  const items: MenuProps["items"] = useMemo(() => {
    const { id } = record;

    const items: MenuProps["items"] = [
      {
        key: "1",
        label: (
          <Row id={id} onClick={onView}>
            View
          </Row>
        )
      }
    ];

    if (page === MAILING_PAGES.Outbox) {
      hasAccess("MailingEmails", "Resend") &&
        items.push({
          key: "3",
          label: (
            <Row id={id} onClick={onResend}>
              Resend
            </Row>
          )
        });
    }

    if (page === MAILING_PAGES.Scheduled || page === MAILING_PAGES.Recurring) {
      hasAccess("MailingScheduled", "Delete") &&
        items.push({
          key: "2",
          label: (
            <Row id={id} onClick={onDelete}>
              Delete
            </Row>
          )
        });
    }

    if (page === MAILING_PAGES.Outbox) {
      hasAccess("MailingEmails", "Delete") &&
        items.push({
          key: "2",
          label: (
            <Row id={id} onClick={onDelete}>
              Delete
            </Row>
          )
        });
    }

    return items;
  }, [record, hasAccess, onDelete, onResend, onView, page]);

  const sendBtn: ReactNode = useMemo(() => {
    const { id, email } = record;

    const disabled = !EMAIL_RULE.test(email);

    return disabled ? (
      <Tooltip placement="top" title="Fix invalid email first">
        <Button className="mr-4" type="text" icon={<SendOutlined />} disabled />
      </Tooltip>
    ) : (
      <Button className="mr-4" type="text" icon={<SendOutlined />} id={id} onClick={onSend} />
    );
  }, [record, onSend]);

  return (
    <Row>
      {hasAccess("MailingEmails", "Send") && page === 0 && sendBtn}
      <Dropdown menu={{ items }} trigger={["click"]}>
        <EllipsisOutlined onClick={(e) => e.stopPropagation()} />
      </Dropdown>
    </Row>
  );
};
